import React, { useState, useMemo } from 'react';
import {
  InformationCircleIcon,
  ServerStackIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import TimelineView from './TimelineView';
import { addServiceIP, removeServiceIP } from '../api/client';
import CopyButton from './CopyButton';

function formatTimestamp(ts) {
  if (!ts) return '\u2014';
  try {
    return format(parseISO(ts), 'MMM d, yyyy HH:mm:ss');
  } catch {
    return ts;
  }
}

export default function ServiceIPEventsTab({
  excludedSignIn = [],
  excludedExchange = [],
  excludedSharePoint = [],
  excludedIPs = [],
  totalExcluded = 0,
  totalEvents = 0,
  serviceIPCount = 0,
  serviceIPs = [],
  onServiceIPsChanged,
}) {
  const [showTimeline, setShowTimeline] = useState(false);
  const [showServiceIPList, setShowServiceIPList] = useState(true);
  const [newIP, setNewIP] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  // Both handlers used to `catch { /* ignore */ }`, so a rejected add or remove
  // left the button looking like it simply did nothing. That is how the
  // global-admin gate on these endpoints went unnoticed.
  const [addError, setAddError] = useState(null);
  const [removeError, setRemoveError] = useState(null);

  const handleAddServiceIP = async () => {
    if (!newIP.trim()) return;
    setAdding(true);
    setAddError(null);
    try {
      await addServiceIP({ ipAddress: newIP.trim(), label: newLabel.trim() || newIP.trim(), category: 'Custom' });
      if (onServiceIPsChanged) {
        onServiceIPsChanged(prev => [...prev, newIP.trim()]);
      }
      setNewIP('');
      setNewLabel('');
    } catch (err) {
      setAddError(err?.message || 'Failed to add service IP.');
    }
    setAdding(false);
  };

  const handleRemoveServiceIP = async (ip) => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeServiceIP(ip);
      if (onServiceIPsChanged) {
        onServiceIPsChanged(prev => prev.filter(i => i !== ip));
      }
      setConfirmRemove(null);
    } catch (err) {
      setRemoveError({ ip, message: err?.message || 'Failed to remove service IP.' });
    }
    setRemoving(false);
  };

  const filteredServiceIPs = useMemo(() => {
    if (!filterQuery.trim()) return serviceIPs;
    const q = filterQuery.toLowerCase();
    return serviceIPs.filter(ip => ip.toLowerCase().includes(q));
  }, [serviceIPs, filterQuery]);

  // Aggregate excluded events by IP
  const ipSummary = useMemo(() => {
    const counts = {};

    excludedSignIn.forEach((evt) => {
      const ip = evt.IPAddress || 'Unknown';
      if (!counts[ip]) counts[ip] = { signIn: 0, exchange: 0, sharePoint: 0 };
      counts[ip].signIn += 1;
    });

    excludedExchange.forEach((evt) => {
      const ip = evt.IPAddress || 'Unknown';
      if (!counts[ip]) counts[ip] = { signIn: 0, exchange: 0, sharePoint: 0 };
      counts[ip].exchange += 1;
    });

    excludedSharePoint.forEach((evt) => {
      const ip = evt.IPAddress || 'Unknown';
      if (!counts[ip]) counts[ip] = { signIn: 0, exchange: 0, sharePoint: 0 };
      counts[ip].sharePoint += 1;
    });

    return Object.entries(counts)
      .map(([ip, c]) => ({
        ipAddress: ip,
        signInCount: c.signIn,
        exchangeCount: c.exchange,
        sharePointCount: c.sharePoint,
        totalCount: c.signIn + c.exchange + c.sharePoint,
      }))
      .sort((a, b) => b.totalCount - a.totalCount);
  }, [excludedSignIn, excludedExchange, excludedSharePoint]);

  const serviceIPListSection = (
    <div className="card p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
        <button onClick={() => setShowServiceIPList(prev => !prev)} className="flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-gray-100">
          {showServiceIPList ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
          Configured Service IPs ({serviceIPs.length})
        </button>
      </div>
      {showServiceIPList && (
        <div className="p-4 space-y-3">
          {/* Add form */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newIP}
              onChange={e => setNewIP(e.target.value)}
              placeholder="IP address"
              className="input-field py-1.5 text-sm flex-1 max-w-[200px]"
              onKeyDown={e => e.key === 'Enter' && handleAddServiceIP()}
            />
            <input
              type="text"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="Label (optional)"
              className="input-field py-1.5 text-sm flex-1 max-w-[200px]"
              onKeyDown={e => e.key === 'Enter' && handleAddServiceIP()}
            />
            <button
              onClick={handleAddServiceIP}
              disabled={adding || !newIP.trim()}
              className="btn-primary py-1.5 text-sm flex items-center gap-1"
            >
              <PlusIcon className="h-4 w-4" />
              Add
            </button>
          </div>
          {addError && (
            <p className="text-xs text-red-400">{addError}</p>
          )}
          {/* Filter */}
          {serviceIPs.length > 10 && (
            <input
              type="text"
              value={filterQuery}
              onChange={e => setFilterQuery(e.target.value)}
              placeholder="Filter IPs..."
              className="input-field py-1 text-xs w-48"
            />
          )}
          {/* IP list */}
          <div className="max-h-[300px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-800 text-gray-500">
                  <th className="text-left px-3 py-2 font-medium">IP Address</th>
                  <th className="text-right px-3 py-2 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {filteredServiceIPs.map(ip => (
                  <tr key={ip} className="hover:bg-gray-800/30">
                    <td className="px-3 py-1.5 font-mono text-gray-300 group">
                      {ip}<CopyButton text={ip} />
                      {removeError?.ip === ip && (
                        <span className="block font-sans text-red-400 mt-0.5">{removeError.message}</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {confirmRemove === ip ? (
                        <span className="flex items-center gap-1 justify-end">
                          <button onClick={() => handleRemoveServiceIP(ip)} disabled={removing} className="text-red-400 hover:text-red-300 text-xs">Yes</button>
                          <button onClick={() => setConfirmRemove(null)} className="text-gray-500 hover:text-gray-300 text-xs ml-1">No</button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirmRemove(ip)} className="text-gray-600 hover:text-red-400">
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredServiceIPs.length === 0 && (
                  <tr><td colSpan={2} className="px-3 py-4 text-center text-gray-600">{serviceIPs.length === 0 ? 'No service IPs configured' : 'No matches'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  if (totalExcluded === 0) {
    return (
      <div className="space-y-6">
        <div className="card text-center py-12">
          <ServerStackIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-400 mb-2">
            No Service IP Events Excluded
          </h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            {serviceIPCount > 0
              ? `None of the IP addresses in this investigation matched the ${serviceIPCount} configured service IPs. All events are shown in the Timeline and IP Addresses tabs.`
              : 'No service IPs are configured. Add known service/corporate IPs below to automatically filter them from investigation results.'}
          </p>
        </div>
        {serviceIPListSection}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="card bg-gray-800/50 border-gray-700">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-gray-300">
              <span className="font-semibold text-gray-100">{totalExcluded}</span> of{' '}
              <span className="font-semibold text-gray-100">{totalEvents}</span> events
              were excluded because they originated from known service IP addresses.
              These events are hidden from the main Timeline and IP Address tabs.
            </p>
          </div>
        </div>
      </div>

      {/* Summary Table */}
      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-800">
          <h3 className="text-sm font-semibold text-gray-300">
            Excluded IPs Summary
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  IP Address
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  Sign-In Events
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  Exchange Events
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  SharePoint Events
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {ipSummary.map((row) => (
                <tr key={row.ipAddress} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-200 group">
                    {row.ipAddress}<CopyButton text={row.ipAddress} />
                  </td>
                  <td className="px-4 py-3 text-center text-gray-400 font-mono">
                    {row.signInCount}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-400 font-mono">
                    {row.exchangeCount}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-400 font-mono">
                    {row.sharePointCount}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-300 font-mono font-semibold">
                    {row.totalCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Configured Service IPs */}
      {serviceIPListSection}

      {/* Excluded Events Timeline */}
      {excludedSignIn.length > 0 && (
        <div>
          <button
            onClick={() => setShowTimeline(!showTimeline)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition-colors mb-3"
          >
            {showTimeline ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
            {showTimeline ? 'Hide' : 'Show'} excluded sign-in events ({excludedSignIn.length})
          </button>
          {showTimeline && (
            <TimelineView events={excludedSignIn} />
          )}
        </div>
      )}
    </div>
  );
}
